import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PhoneInput, { validatePhone, toFullPhoneNumber } from '../../components/PhoneInput';
import SEO from '../../components/SEO';
import '../../styles/Register.scss';

const GOOGLE_CLIENT_ID = "957736829829-ebpu5bvvadglra8ujbjb8sb384ou4k27.apps.googleusercontent.com";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: unknown) => void;
          renderButton: (el: HTMLElement, opts: unknown) => void;
          prompt: () => void;
        };
      };
    };
  }
}

type Role = 'researcher' | 'institution' | 'publisher';

function Input({ placeholder, type = 'text', value, onChange, error }: {
  label?: string; type?: string; placeholder: string;
  value: string; onChange: (v: string) => void; error?: string;
}) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <div>
      <div className="field-wrap">
        <input
          type={isPassword ? (show ? 'text' : 'password') : type}
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
          className={`text-input${error ? ' text-input--error' : ''}${isPassword ? ' text-input--password' : ''}`}
        />
        {isPassword && (
          <button type="button" onClick={() => setShow(!show)} className="field-toggle">
            {show
              ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
              : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            }
          </button>
        )}
      </div>
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

function GoogleButton({ onCredential }: { onCredential: (credential: string) => void }) {
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleCredentialResponse = useCallback((response: { credential?: string }) => {
    if (!response?.credential) return;
    setGoogleLoading(true);
    onCredential(response.credential);
  }, [onCredential]);

  useEffect(() => {
    const initializeGoogle = () => {
      if (window.google && googleBtnRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: 400,
        });
      }
    };
    if (!window.google) {
      const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existing) {
        existing.addEventListener('load', initializeGoogle);
      } else {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = initializeGoogle;
        document.body.appendChild(script);
      }
    } else {
      initializeGoogle();
    }
  }, [handleCredentialResponse]);

  return (
    <div style={{ position: 'relative', width: '100%', height: 44 }}>
      {/* Visual layer — pointer-events none so clicks pass through */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff',
        fontSize: 13, fontWeight: 600, color: '#374151',
      }}>
        <svg width="18" height="18" viewBox="0 0 18 18">
          <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
          <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" />
          <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" />
          <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" />
        </svg>
        {googleLoading ? 'Signing in...' : 'Continue with Google'}
      </div>
      {/* Google's real button — invisible but clickable on top */}
      <div ref={googleBtnRef} style={{
        position: 'absolute', inset: 0, zIndex: 2,
        overflow: 'hidden', opacity: 0.01, borderRadius: 8,
      }} />
    </div>
  );
}

const DEFAULT_COUNTRY = 'IN';

export default function Register() {

  const navigate = useNavigate();
  const location = useLocation();

  const roleFromState = (location.state as { role?: string } | null)?.role;
  const [role, setRole] = useState<Role>(
    roleFromState === 'institution' || roleFromState === 'publisher' ? roleFromState : 'researcher'
  );
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY);

  const { loginWithGoogle } = useAuth();

  const handleGoogleCredential = useCallback(async (credential: string) => {
    try {
      const isNew = await loginWithGoogle(credential);
      navigate(isNew ? '/choose-plan' : '/dashboard', { replace: true });
    } catch (err: any) {
      setGlobalError(err.message || 'Google sign-in failed. Please try again.');
    }
  }, [loginWithGoogle, navigate]);

  const [fields, setFields] = useState({
    full_name: '', organisation: '', email: '',
    phone: '', password: '', confirm_password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (key: string) => (val: string) => {
    setFields(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: '' }));
    setGlobalError('');
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!fields.full_name.trim()) e.full_name = 'Name is required.';
    if (role !== 'researcher' && !fields.organisation.trim())
      e.organisation = 'Organisation name is required.';
    if (!fields.email.trim()) e.email = 'Email is required.';

    const phoneError = validatePhone(phoneCountry, fields.phone);
    if (phoneError) e.phone = phoneError;

    if (fields.password.length < 8) e.password = 'Password must be at least 8 characters.';
    if (fields.password !== fields.confirm_password)
      e.confirm_password = 'Passwords do not match.';
    if (!agreed) e.agreed = 'You must agree to the terms.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    setGlobalError('');
    try {
      await AuthAPI.register({
        email: fields.email.trim().toLowerCase(),
        password: fields.password,
        confirm_password: fields.confirm_password,
        full_name: fields.full_name.trim(),
        phone: toFullPhoneNumber(phoneCountry, fields.phone),
        role,
        organisation: fields.organisation.trim(),
      });
      navigate('/verify-otp', { state: { email: fields.email.trim().toLowerCase() }, replace: true });
    } catch (err: any) {
      setGlobalError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (newRole: Role) => {
    setRole(newRole);
    setFields({ full_name: '', organisation: '', email: '', phone: '', password: '', confirm_password: '' });
    setErrors({});
    setGlobalError('');
    setAgreed(false);
    setPhoneCountry(DEFAULT_COUNTRY); // reset the country code back to default too
  };

  const roleOptions: { id: Role; label: string; desc: string }[] = [
    { id: 'researcher', label: 'Researcher', desc: 'Individual researcher or student' },
    { id: 'institution', label: 'Institution', desc: 'University, college or school' },
    { id: 'publisher', label: 'Publisher', desc: 'Journal or academic publisher' },
  ];

  return (
    <div className="register-page">
      <SEO title="Register" noIndex />
      {/* Left panel */}
      <div className="hero-panel">
        <div className="hero-panel__blob-1" />
        <div className="hero-panel__blob-2" />
        <Link to="/" className="hero-panel__logo">
          <div
            style={{
              background: "#fff",
              border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: "4px",
              padding: "6px 10px",
              boxShadow: "0 0 10px rgba(255,255,255,0.25)",
              animation: "borderBlink 2s ease-in-out infinite",
            }}
          >
            <img
              src="/manna-plag-logo-horizontal.png?v=2"
              alt="Manna Plag"
              style={{
                height: 28,
                objectFit: "contain",
              }}
            />
          </div>
        </Link>
        <div className="hero-panel__main">
          <h2 className="hero-panel__title">Academic integrity<br />starts here</h2>
          <p className="hero-panel__subtitle">
            Join researchers, institutions, and publishers who trust Manna Plag to verify the originality of their work.
          </p>
          <div className="hero-features">
            {[
              { title: '1 free checks', desc: 'No credit card needed to get started' },
              { title: 'Results in minutes', desc: 'Full report delivered fast' },
              { title: 'AI detection included', desc: 'Identify AI-generated content' },
              { title: 'Corpus updated daily', desc: 'Always checked against latest research' },
            ].map(f => (
              <div key={f.title} className="hero-feature">
                <div className="hero-feature__icon">
                  <span>✓</span>
                </div>
                <div>
                  <p className="hero-feature__title">{f.title}</p>
                  <p className="hero-feature__desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-panel__footer">
          <p>© {new Date().getFullYear()} Manna Analytics Private Limited</p>
        </div>
      </div>

      {/* Right panel */}
      <div className="form-panel">
        <div className="form-container bg-white rounded-xl border border-gray-100 shadow-sm p-8">
          <Link to="/" className="mobile-logo">
            {/* <div className="mobile-logo__icon">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path d="M8 1L14 4.5V11.5L8 15L2 11.5V4.5L8 1Z" stroke="white" strokeWidth="1.5" fill="none" />
                <path d="M8 5L11 7V11L8 13L5 11V7L8 5Z" fill="white" />
              </svg>
            </div>
            <span className="mobile-logo__text">Manna Plag</span> */}
            <img
              src="/manna-plag-logo-horizontal.png?v=2"
              alt="Manna Plag"
              style={{
                height: 28,
                objectFit: "contain",
              }}
            />
          </Link>

          <h1 className="form-title">Create your account</h1>
          <p className="form-subtitle">
            Already have an account?{' '}
            <Link to="/login">Sign in</Link>
          </p>

          {/* Role selector */}
          <div className="role-selector">
            <p className="role-selector__label">Sign up as <span className="required">*</span></p>
            <div className="role-options">
              {roleOptions.map(r => (
                <label key={r.id} className={`role-option${role === r.id ? ' role-option--checked' : ''}`}>
                  <input type="radio" name="role" value={r.id}
                    checked={role === r.id} onChange={() => handleRoleChange(r.id)} />
                  <span className="role-option__label">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Dynamic fields */}
          <div className="fields">
            <Input
              placeholder={role === 'researcher' ? 'Full Name *' : 'Contact Name *'}
              value={fields.full_name} onChange={set('full_name')} error={errors.full_name} />

            {role !== 'researcher' && (
              <Input
                placeholder={role === 'institution' ? 'Institution Name *' : 'Company Name *'}
                value={fields.organisation} onChange={set('organisation')} error={errors.organisation} />
            )}

            <Input type="email" placeholder="Email *"
              value={fields.email} onChange={set('email')} error={errors.email} />

            <PhoneInput
              country={phoneCountry} onCountryChange={setPhoneCountry}
              value={fields.phone} onChange={set('phone')} error={errors.phone} />

            <Input type="password" placeholder="Password *"
              value={fields.password} onChange={set('password')} error={errors.password} />

            <Input type="password" placeholder="Confirm Password *"
              value={fields.confirm_password} onChange={set('confirm_password')} error={errors.confirm_password} />
          </div>

          {/* Terms */}
          <label className="terms-row">
            <input type="checkbox" checked={agreed}
              onChange={e => { setAgreed(e.target.checked); setErrors(ev => ({ ...ev, agreed: '' })); }} />
            <span>
              I agree with the{' '}
              <Link to="/privacy">Privacy Policy</Link>
              {' '}and{' '}
              <Link to="/terms">Terms &amp; Conditions</Link>
            </span>
          </label>
          {errors.agreed && <p className="terms-error">{errors.agreed}</p>}

          {/* Global error */}
          {globalError && (
            <div className="global-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p>{globalError}</p>
            </div>
          )}

          {/* Submit */}
          <button onClick={handleSubmit} disabled={loading} className="submit-btn">
            {loading ? 'Creating account...' : 'Create Account'}
          </button>

          {/* Google — secondary */}
          <div className="divider-row">
            <div className="divider-row__line" />
            <span className="divider-row__text">or</span>
            <div className="divider-row__line" />
          </div>
          <GoogleButton onCredential={handleGoogleCredential} />

          <p className="footer-note">
            By creating an account you agree to our{' '}
            <Link to="/terms">Terms</Link>,{' '}
            <Link to="/privacy">Privacy Policy</Link> and{' '}
            <Link to="/cancellation">Refund Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}